import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import { Option } from 'carbon-react/lib/components/select';
import SimpleSelect, { SimpleSelectProps } from 'carbon-react/lib/components/select/simple-select';
import Typography from 'carbon-react/lib/components/typography';

import { ForecastFormElementIds } from '../../../types';
import { NewConsolidatedForecastDataSource } from '../../../../dashboard/types';

import styles from './consolidated-forecast-datasource.scss';

export type ConsolidatedForecastDatasourceProps = Omit<
  SimpleSelectProps,
  'name' | 'id' | 'children' | 'inputHint' | 'label'
> & {
  disableCombineSubEntityForecatstsOption?: boolean;
  type: 'budget' | 'forecast';
};

export const ConsolidatedForecastDatasource = ({ type, ...props }: ConsolidatedForecastDatasourceProps) => {
  const intl = useIntl();

  const [label, inputHint] = useMemo(() => {
    if (type === 'budget') {
      return [
        intl.formatMessage({
          id: 'predict.create_forecast.add_predictions',
          defaultMessage: 'Add predictions'
        }),
        intl.formatMessage({
          id: 'predict.create_forecast.you_can_edit_and_add_to_your_budget_later',
          defaultMessage: 'You can edit and add to your budget later'
        })
      ];
    } else {
      return [
        intl.formatMessage({
          id: 'predict.create_forecast.add_predictions',
          defaultMessage: 'Add predictions'
        }),
        intl.formatMessage({
          id: 'predict.create_forecast.you_can_edit_and_add_to_your_forecast_later',
          defaultMessage: 'You can edit and add to your forecast later'
        })
      ];
    }
  }, [intl, type]);

  return (
    <SimpleSelect
      className={styles.select}
      name={ForecastFormElementIds.Datasource}
      id={ForecastFormElementIds.Datasource}
      label={label}
      inputHint={inputHint}
      required
      listMaxHeight={480}
      {...props}
    >
      {/* Consolidation-specific options */}
      <Option disabled className={styles.listHeaderContainer}>
        <Typography variant="strong" className={styles.listHeader}>
          {intl.formatMessage({
            id: 'predict.create_forecast.consolidation_options.list_header',
            defaultMessage: 'Consolidation options'
          })}
        </Typography>
      </Option>
      {type === 'budget' ? (
        <Option
          text={intl.formatMessage({
            id: 'predict.create_forecast.combine_existing_budgets_from_multiple_orgs',
            defaultMessage: 'Combine existing budgets from multiple organisations'
          })}
          value={NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING_SUB_ENTITY_FORECASTS}
        />
      ) : (
        <Option
          text={intl.formatMessage({
            id: 'predict.create_forecast.combine_existing_forecasts_from_multiple_orgs',
            defaultMessage: 'Combine existing forecasts from multiple organisations'
          })}
          value={NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING_SUB_ENTITY_FORECASTS}
          disabled={props.disableCombineSubEntityForecatstsOption}
        />
      )}
      <Option disabled className={styles.hrContainer}>
        <Box className={styles.hr} as="hr" />
      </Option>

      {/* Standard options */}
      <Option disabled className={styles.listHeaderContainer}>
        <Typography variant="strong" className={clsx(styles.listHeader, styles.listHeader2)}>
          {intl.formatMessage({
            id: 'predict.create_forecast.standard_options.list_header',
            defaultMessage: 'Standard options'
          })}
        </Typography>
      </Option>
      <Option
        text={intl.formatMessage({
          id: 'predict.create_forecast.use_predictions_based_on_last_years_actuals_2',
          defaultMessage: "Use predictions based on last year's actuals"
        })}
        value={NewConsolidatedForecastDataSource.CREATE_FROM_ACTUALS}
      />
      <Option
        text={intl.formatMessage({
          id: 'predict.create_forecast.use_futrlis_automated_predictions',
          defaultMessage: "Use Futrli's automated predictions"
        })}
        value={NewConsolidatedForecastDataSource.CREATE_FROM_AI_ASSUMPTIONS}
      />
      {type === 'budget' ? (
        <Option
          text={intl.formatMessage({
            id: 'predict.create_forecast.copy_predictions_from_and_exiting_budget_v2',
            defaultMessage: 'Copy predictions from an existing budget or forecast'
          })}
          value={NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING}
        />
      ) : (
        <Option
          text={intl.formatMessage({
            id: 'predict.create_forecast.copy_predictions_from_and_exiting_budget_v2',
            defaultMessage: 'Copy predictions from an existing budget or forecast'
          })}
          value={NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING}
        />
      )}
      <Option
        text={intl.formatMessage({
          id: 'predict.create_forecast.start_from_scratch_without_predictions',
          defaultMessage: 'Start from scratch without predictions'
        })}
        value={NewConsolidatedForecastDataSource.CREATE_FROM_SCRATCH}
      />
    </SimpleSelect>
  );
};
